import React from 'react'
import { Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Statistic, Table, Tag, Typography, message } from 'antd'
import { listFinancialRecords, createFinancialRecord, updateFinancialRecord, deleteFinancialRecord } from '../../api/financial'
import { formatAmount } from '../../utils/amount'

const { Title } = Typography

export default function FinancialRecords() {
    const [data, setData] = React.useState([])
    const [loading, setLoading] = React.useState(false)
    const [createOpen, setCreateOpen] = React.useState(false)
    const [creating, setCreating] = React.useState(false)
    const [editOpen, setEditOpen] = React.useState(false)
    const [editing, setEditing] = React.useState(false)
    const [selected, setSelected] = React.useState(null)

    const [createForm] = Form.useForm()
    const [editForm] = Form.useForm()

    const fetchData = React.useCallback(async () => {
        setLoading(true)
        try {
            const items = await listFinancialRecords()
            setData(items || [])
        } catch (e) {
            console.error(e)
            message.error(e.message || 'فشل في جلب البيانات')
        } finally {
            setLoading(false)
        }
    }, [])

    React.useEffect(() => { fetchData() }, [fetchData])

    async function onCreate(values) {
        setCreating(true)
        try {
            await createFinancialRecord(values)
            message.success('تم إنشاء السجل بنجاح')
            setCreateOpen(false)
            createForm.resetFields()
            await fetchData()
        } catch (e) {
            message.error(e.message)
        } finally {
            setCreating(false)
        }
    }

    function openEdit(record) {
        setSelected(record)
        editForm.setFieldsValue(record)
        setEditOpen(true)
    }

    async function onEdit(values) {
        if (!selected) return
        setEditing(true)
        try {
            await updateFinancialRecord(selected.id, values)
            message.success('تم تحديث السجل')
            setEditOpen(false)
            setSelected(null)
            await fetchData()
        } catch (e) {
            message.error(e.message)
        } finally {
            setEditing(false)
        }
    }

    async function onDelete(id) {
        try {
            await deleteFinancialRecord(id)
            message.success('تم الحذف')
            await fetchData()
        } catch (e) {
            message.error(e.message)
        }
    }

    const columns = [
        { title: 'ID', dataIndex: 'id', key: 'id' },
        {
            title: 'رقم الطلب', dataIndex: 'orderId', key: 'orderId',
            render: (v) => v ? <Tag color="blue">#{v}</Tag> : '-'
        },
        { title: 'اسم المتجر', dataIndex: 'shopName', key: 'shopName' },
        {
            title: 'إجمالي العميل', dataIndex: 'userTotal', key: 'userTotal',
            render: (v) => formatAmount(v)
        },
        {
            title: 'إجمالي المتجر', dataIndex: 'shopTotal', key: 'shopTotal',
            render: (v) => formatAmount(v)
        },
        {
            title: 'الربح', dataIndex: 'revenueFromShop', key: 'revenueFromShop',
            render: (v) => <Text strong style={{ color: '#52c41a' }}>{formatAmount(v)}</Text>
        },
        { title: 'السائق', dataIndex: 'assignedDriver', key: 'assignedDriver' },
        {
            title: 'المصدر', dataIndex: 'source', key: 'source',
            render: (v) => <Tag color={v === 'system' ? 'purple' : 'orange'}>{v}</Tag>
        },
        {
            title: 'الحالة', dataIndex: 'status', key: 'status',
            render: (v) => (
                <Tag color={v === 'collected' ? 'green' : 'gold'}>
                    {v === 'collected' ? 'تم التحصيل' : 'قيد التحصيل'}
                </Tag>
            )
        },
        {
            title: 'التاريخ', dataIndex: 'createdAt', key: 'createdAt',
            render: (v) => v ? new Date(v).toLocaleString('ar-EG') : '-'
        },
        {
            title: '', key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button size="small" onClick={() => openEdit(record)}>تعديل</Button>
                    <Popconfirm title="حذف هذا السجل؟" onConfirm={() => onDelete(record.id)}>
                        <Button size="small" danger>حذف</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ]

    const { Text } = Typography

    const totals = React.useMemo(() => {
        return data.reduce((acc, curr) => ({
            userTotal: acc.userTotal + (Number(curr.userTotal) || 0),
            shopTotal: acc.shopTotal + (Number(curr.shopTotal) || 0),
            revenue: acc.revenue + (Number(curr.revenueFromShop) || 0)
        }), { userTotal: 0, shopTotal: 0, revenue: 0 })
    }, [data])

    return (
        <div style={{ maxWidth: 1200, margin: '40px auto', padding: '0 16px', direction: 'rtl' }}>
            <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <Title level={3} style={{ margin: 0 }}>السجلات المالية</Title>
                <Button type="primary" onClick={() => setCreateOpen(true)}>إضافة سجل يدوي</Button>
            </Space>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
                <Card size="small">
                    <Statistic title="إجمالي مدفوعات العملاء" value={totals.userTotal} formatter={formatAmount} />
                </Card>
                <Card size="small">
                    <Statistic title="إجمالي مستحقات المتاجر" value={totals.shopTotal} formatter={formatAmount} />
                </Card>
                <Card size="small">
                    <Statistic title="إجمالي الأرباح" value={totals.revenue} formatter={formatAmount} valueStyle={{ color: '#52c41a' }} />
                </Card>
            </div>
            <Card bodyStyle={{ padding: 0 }}>
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    pagination={{ pageSize: 20 }}
                    scroll={{ x: 800 }}
                />
            </Card>

            {/* Create Modal */}
            <Modal
                title="إضافة سجل مالي"
                open={createOpen}
                onCancel={() => setCreateOpen(false)}
                onOk={() => createForm.submit()}
                confirmLoading={creating}
                okText="إضافة"
                cancelText="إلغاء"
            >
                <Form form={createForm} layout="vertical" onFinish={onCreate} initialValues={{ source: 'admin', assignedDriver: '-', status: 'pending_collection' }}>
                    <Form.Item label="رقم الطلب (اختياري)" name="orderId">
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="اسم المتجر" name="shopName" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item label="إجمالي العميل" name="userTotal" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="إجمالي المتجر" name="shopTotal" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="الربح" name="revenueFromShop" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="السائق" name="assignedDriver">
                        <Input />
                    </Form.Item>
                    <Form.Item label="المصدر" name="source">
                        <Input />
                    </Form.Item>
                    <Form.Item label="الحالة" name="status">
                        <Select options={[
                            { label: 'قيد التحصيل', value: 'pending_collection' },
                            { label: 'تم التحصيل', value: 'collected' }
                        ]} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Edit Modal */}
            <Modal
                title="تعديل سجل مالي"
                open={editOpen}
                onCancel={() => setEditOpen(false)}
                onOk={() => editForm.submit()}
                confirmLoading={editing}
                okText="حفظ"
                cancelText="إلغاء"
            >
                <Form form={editForm} layout="vertical" onFinish={onEdit}>
                    <Form.Item label="رقم الطلب (اختياري)" name="orderId">
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="اسم المتجر" name="shopName" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item label="إجمالي العميل" name="userTotal" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="إجمالي المتجر" name="shopTotal" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="الربح" name="revenueFromShop" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="السائق" name="assignedDriver">
                        <Input />
                    </Form.Item>
                    <Form.Item label="المصدر" name="source">
                        <Input />
                    </Form.Item>
                    <Form.Item label="الحالة" name="status">
                        <Select options={[
                            { label: 'قيد التحصيل', value: 'pending_collection' },
                            { label: 'تم التحصيل', value: 'collected' }
                        ]} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    )
}
